import dayjs from 'dayjs';
import { createContext, useContext, useMemo } from 'react';
import {
  FundraisingDistributionLocalStorageSearchFilter,
  FundraisingDistributionSearchFilter,
} from 'src/modules/fundraiser/models/fundraisingDistributionLocalStorageSearchFilter';
import { LocalStorageKeys } from '../models/enums/localStorageKeys.enum';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { DateHelper } from '../helpers/dateHelper';

export interface FundraiserContextSettings {
  fundraisingDistributionSearchFilter: FundraisingDistributionSearchFilter;
  fundraisingDistributionStorageSearchFilter: FundraisingDistributionLocalStorageSearchFilter;
  setFundraisingDistributionStorageSearchFilter: React.Dispatch<React.SetStateAction<FundraisingDistributionLocalStorageSearchFilter>>;
}

export const FundraiserContext = createContext<FundraiserContextSettings | undefined>(undefined);

export const useFundraiserContext = () => {
  const context = useContext(FundraiserContext);
  if (context === undefined) {
    throw new Error('useFundraiserContext must be within FundraiserContextProvider');
  }
  return context;
};

interface Props {
  children: JSX.Element;
}

export const FundraiserContextProvider = ({ children }: Props) => {
  const [fundraisingDistributionStorageSearchFilter, setFundraisingDistributionStorageSearchFilter] = useLocalStorage(
    LocalStorageKeys.FundraisingDistributionSearchFilter,
    {
      start: dayjs().startOf('month').toDate(),
      end: new Date(),
    } as FundraisingDistributionLocalStorageSearchFilter,
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const fundraisingDistributionSearchFilter: FundraisingDistributionSearchFilter = useMemo(() => {
    return {
      start: DateHelper.postDateTime(fundraisingDistributionStorageSearchFilter.start),
      end: DateHelper.postDateTime(fundraisingDistributionStorageSearchFilter.end),
    } as FundraisingDistributionSearchFilter;
  }, [fundraisingDistributionStorageSearchFilter.end, fundraisingDistributionStorageSearchFilter.start]);

  const fundraiserContext = useMemo(
    () => ({
      fundraisingDistributionStorageSearchFilter,
      setFundraisingDistributionStorageSearchFilter,
      fundraisingDistributionSearchFilter,
    }),
    [fundraisingDistributionStorageSearchFilter, setFundraisingDistributionStorageSearchFilter, fundraisingDistributionSearchFilter],
  );

  return <FundraiserContext.Provider value={fundraiserContext}>{children}</FundraiserContext.Provider>;
};
